import { useState, useRef, useEffect } from "react";
import {
  Play,
  Pause,
  Download,
  MoreHorizontal,
  Clock,
  Sparkles,
  Package,
  X,
  Sliders,
  Trash2,
} from "lucide-react";
import type { SoundEffect } from "../types";
import { soundsApi } from "../lib/api";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const ANIMATION_DURATIONS = {
  slideIn: 500,
  fadeInUp: 400,
  staggerDelay: 50,
  maxStaggerDelay: 300,
} as const;

interface SoundTableProps {
  sounds: SoundEffect[];
  loading: boolean;
  error: string | null;
  onPlaySound: (sound: SoundEffect) => void;
  onRefresh: () => Promise<void>;
  selectedFolderId: string | null;
  animationMode: "none" | "initial" | "new-sound";
  newSoundId?: string;
  selectionMode?: boolean;
  selectedSounds?: Set<string>;
  onToggleSelection?: (soundId: string) => void;
  onBatchExport?: () => void;
}

export function SoundTable({
  sounds,
  loading,
  error,
  onPlaySound,
  onRefresh,
  selectedFolderId,
  animationMode,
  newSoundId,
  selectionMode = false,
  selectedSounds = new Set(),
  onToggleSelection,
  onBatchExport,
}: SoundTableProps) {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const lastDownloadTime = useRef<number>(0);

  // Click outside handler for menu popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Check if the click is outside all menu-related elements
      if (
        openMenuId &&
        !target.closest("[data-menu-trigger]") &&
        !target.closest("[data-menu-content]")
      ) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [openMenuId]);

  // Filter sounds by the current folder
  const folderSounds = selectedFolderId
    ? sounds.filter((sound) => sound.folderId === selectedFolderId)
    : sounds;

  const handlePlaySound = (sound: SoundEffect) => {
    if (playingId === sound.id) {
      setPlayingId(null);
    } else {
      setPlayingId(sound.id);
      onPlaySound(sound);
    }
  };

  const handleDownloadSound = async (sound: SoundEffect) => {
    if (!sound.downloadUrl) return;

    // Prevent concurrent downloads
    if (downloadingId) {
      console.log("Another download is in progress, please wait...");
      setDownloadError("Please wait for the current download to complete");
      return;
    }

    // Implement download throttling - wait at least 1 second between downloads
    const now = Date.now();
    const timeSinceLastDownload = now - lastDownloadTime.current;
    if (timeSinceLastDownload < 1000) {
      console.log("Download throttled, waiting...");
      setDownloadError("Please wait a moment between downloads...");
      setTimeout(() => {
        setDownloadError(null); // Clear the message
        handleDownloadSound(sound);
      }, 1000 - timeSinceLastDownload);
      return; // Don't set downloading state yet
    }

    // Only set downloading state when actually proceeding with download
    setDownloadError(null);
    setDownloadingId(sound.id);
    lastDownloadTime.current = now;

    try {
      // Parse the existing URL and add parameters properly
      const url = new URL(sound.downloadUrl, window.location.origin);
      url.searchParams.set("download", "true");
      url.searchParams.set("t", Date.now().toString());

      console.log("Downloading from URL:", url.toString());

      const blob = await soundsApi.download(url.toString());
      console.log("Blob received, size:", blob.size, "type:", blob.type);

      // Verify blob is valid
      if (!blob || blob.size === 0) {
        throw new Error("Received empty or invalid audio file");
      }

      // Simple, immediate download pattern (matches export logic)
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${sound.title}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl); // Immediate cleanup

      console.log("Download completed successfully for:", sound.title);
    } catch (error) {
      console.error("Error downloading sound:", error);
      console.error(
        "Stack trace:",
        error instanceof Error ? error.stack : "No stack trace",
      );
      setDownloadError(
        error instanceof Error
          ? error.message
          : "Failed to download sound. Please try again.",
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteSound = async (soundId: string) => {
    setDeletingId(soundId);
    setDownloadError(null);

    try {
      await soundsApi.delete(soundId);
      setDeleteConfirm(null);
      setOpenMenuId(null);
      await onRefresh(); // Refresh the sounds list
    } catch (error) {
      console.error("Error deleting sound:", error);
      setDownloadError(
        error instanceof Error
          ? error.message
          : "Failed to delete sound. Please try again.",
      );
    } finally {
      setDeletingId(null);
    }
  };

  const getSoundItemStyle = (sound: SoundEffect, index: number) => {
    // New sound gets special animation
    if (animationMode === "new-sound" && sound.id === newSoundId) {
      return {
        animation: `slideIn ${ANIMATION_DURATIONS.slideIn}ms cubic-bezier(0.16, 1, 0.3, 1) forwards`,
      };
    }

    // Initial load gets staggered animation
    if (animationMode === "initial") {
      return {
        opacity: 0,
        transform: "translateY(20px)",
        animation: `fadeInUp ${ANIMATION_DURATIONS.fadeInUp}ms cubic-bezier(0.16, 1, 0.3, 1) ${Math.min(index * ANIMATION_DURATIONS.staggerDelay, ANIMATION_DURATIONS.maxStaggerDelay)}ms forwards`,
      };
    }

    // No animation - immediate display
    return {
      opacity: 1,
      transform: "translateY(0)",
    };
  };

  const formatDate = (dateInput: Date | string) => {
    const date =
      typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return "Just now";
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const renderSoundRow = (sound: SoundEffect, index: number) => (
    <TableRow
      key={sound.id}
      className={cn(
        "group hover:bg-surface-hover transition-colors",
        animationMode === "new-sound" &&
          sound.id === newSoundId &&
          "bg-generation-active/10",
      )}
      style={getSoundItemStyle(sound, index)}
    >
      {/* Selection checkbox */}
      {selectionMode && onToggleSelection && (
        <TableCell className="w-12">
          <input
            type="checkbox"
            checked={selectedSounds.has(sound.id)}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelection(sound.id);
            }}
            className="w-4 h-4 rounded cursor-pointer"
          />
        </TableCell>
      )}

      {/* Title */}
      <TableCell className="font-medium">
        <div className="flex items-center space-x-2">
          <span className="line-clamp-1">{sound.title}</span>
          {animationMode === "new-sound" && sound.id === newSoundId && (
            <div className="flex items-center space-x-1 bg-generation-active/20 border border-generation-active/30 text-generation-active px-2 py-1 rounded-full text-xs font-semibold">
              <Sparkles size={10} />
              <span>New</span>
            </div>
          )}
        </div>
      </TableCell>

      {/* Prompt */}
      <TableCell className="max-w-xs">
        <p className="text-sm text-muted-foreground line-clamp-2">
          {sound.prompt}
        </p>
      </TableCell>

      {/* Duration */}
      <TableCell>
        <div className="flex items-center space-x-1">
          <Clock size={12} className="text-primary" />
          <span className="text-sm">{sound.duration.toFixed(1)}s</span>
        </div>
      </TableCell>

      {/* Prompt Influence */}
      <TableCell>
        {sound.promptInfluence !== undefined ? (
          <div className="flex items-center space-x-1">
            <Sliders size={12} className="text-accent" />
            <span className="text-sm">
              {Math.round(sound.promptInfluence * 100)}%
            </span>
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>

      {/* Created At */}
      <TableCell className="text-sm text-muted-foreground">
        {formatDate(sound.createdAt)}
      </TableCell>

      {/* Actions */}
      <TableCell>
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => handlePlaySound(sound)}
            variant={playingId === sound.id ? "default" : "secondary"}
            size="sm"
            className={cn(
              "[&_svg]:group-hover:rotate-0 [&_svg]:hover:rotate-12",
              playingId === sound.id && "animate-pulse-glow",
            )}
          >
            {playingId === sound.id ? (
              <>
                <Pause size={14} />
                <span className="ml-1">Pause</span>
                <div className="audio-bars ml-1">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="audio-bar animate-waveform-pulse"
                      style={{
                        height: `${6 + Math.random() * 6}px`,
                        animationDelay: `${i * 0.1}s`,
                      }}
                    />
                  ))}
                </div>
              </>
            ) : (
              <>
                <Play size={14} />
                <span className="ml-1">Play</span>
              </>
            )}
          </Button>

          <Button
            onClick={() => handleDownloadSound(sound)}
            variant="outline"
            size="sm"
            className="[&_svg]:group-hover:rotate-0 [&_svg]:hover:rotate-12"
            disabled={downloadingId === sound.id}
          >
            {downloadingId === sound.id ? (
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            ) : (
              <Download size={14} />
            )}
          </Button>

          {/* More options dropdown */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              className="opacity-0 group-hover:opacity-100 transition-all duration-200 [&_svg]:group-hover:rotate-0 [&_svg]:hover:rotate-12"
              data-menu-trigger
              onClick={(e) => {
                e.stopPropagation();
                setOpenMenuId(openMenuId === sound.id ? null : sound.id);
              }}
            >
              <MoreHorizontal size={14} />
            </Button>

            {/* Dropdown menu */}
            {openMenuId === sound.id && (
              <div
                className="absolute right-0 top-8 z-20 bg-surface border border-border rounded-lg shadow-lg p-1 min-w-[120px]"
                data-menu-content
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirm(sound.id);
                    setOpenMenuId(null);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-surface-hover transition-colors text-destructive hover:text-destructive-foreground flex items-center space-x-2 rounded-md"
                  disabled={deletingId === sound.id}
                >
                  {deletingId === sound.id ? (
                    <div className="w-4 h-4 border-2 border-destructive border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Trash2 size={14} />
                  )}
                  <span>Delete</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </TableCell>
    </TableRow>
  );

  if (loading) {
    return (
      <div className="flex-1 overflow-y-auto py-12">
        <div>
          <Table>
            <TableHeader>
              <TableRow>
                {selectionMode && <TableHead className="w-12"></TableHead>}
                <TableHead>Title</TableHead>
                <TableHead>Prompt</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Influence</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(8)].map((_, index) => (
                <TableRow key={index}>
                  {selectionMode && (
                    <TableCell>
                      <div className="w-4 h-4 bg-muted rounded skeleton" />
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="h-4 bg-muted rounded skeleton w-32" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-muted rounded skeleton w-48" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-muted rounded skeleton w-12" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-muted rounded skeleton w-12" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-muted rounded skeleton w-16" />
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <div className="h-8 bg-muted rounded skeleton w-16" />
                      <div className="h-8 bg-muted rounded skeleton w-8" />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md">
          <p className="text-destructive">{error}</p>
          <Button onClick={onRefresh} variant="default">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (folderSounds.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto">
            <Sparkles className="w-10 h-10 text-muted-foreground" />
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-medium text-foreground">
              {selectedFolderId ? "No sounds in this folder" : "No sounds yet"}
            </h3>
            <p className="text-muted-foreground">
              Create your first sound effect using the generator below
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto py-8">
      <div>
        {/* Selection mode header */}
        {selectionMode && selectedSounds.size > 0 && (
          <div className="flex items-center justify-between mb-4 mx-8 p-4 bg-muted rounded-lg">
            <span className="text-sm font-medium">
              {selectedSounds.size} sound{selectedSounds.size !== 1 ? "s" : ""}{" "}
              selected
            </span>
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => {
                  // Clear selection
                  selectedSounds.clear();
                  void onRefresh();
                }}
                variant="secondary"
                size="sm"
              >
                Clear
              </Button>
              {onBatchExport && (
                <Button onClick={onBatchExport} variant="default" size="sm">
                  <Package size={14} />
                  <span>Export</span>
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Error Alert */}
        {downloadError && (
          <Alert variant="destructive" className="mb-4 mx-8">
            <AlertDescription className="flex items-center justify-between">
              <span>{downloadError}</span>
              <Button
                onClick={() => setDownloadError(null)}
                variant="ghost"
                size="icon"
                className="h-auto w-auto p-1 text-destructive-foreground hover:text-destructive-foreground/80"
                aria-label="Dismiss"
              >
                <X size={16} />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-2">
            {selectedFolderId ? "Folder Sounds" : "All Sounds"}
          </h2>
          <p className="text-muted-foreground">
            {folderSounds.length} sound{folderSounds.length === 1 ? "" : "s"}
          </p>
        </div>

        {/* Sound table */}
        <Table>
          <TableHeader>
            <TableRow>
              {selectionMode && <TableHead className="w-12"></TableHead>}
              <TableHead>Title</TableHead>
              <TableHead>Prompt</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Influence</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {folderSounds.map((sound, index) => renderSoundRow(sound, index))}
          </TableBody>
        </Table>
      </div>

      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-surface border border-border rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Delete Sound
              </h3>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to delete this sound? This action cannot
                be undone.
              </p>
              <div className="flex space-x-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deletingId === deleteConfirm}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteSound(deleteConfirm)}
                  disabled={deletingId === deleteConfirm}
                >
                  {deletingId === deleteConfirm ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
